import { createAsyncThunk, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import _ from 'lodash';
import Constants from '../constants';
import { IAction, SurveyImage, SurveyLogoPlacement } from '../models';
import * as SurveyImageAPI from '../services/SurveyImageAPI';
import { IApplicationState } from './index';

type ValidationResult<TKey extends string> = {
	isValid: boolean;
	failures: ValidationFailures<TKey>;
};

type ValidationFailures<TKey extends string> = {
	[key in TKey]?: string[];
};

type SurveyImageValidatableKey = 'displayName' | 'file';

type SurveyImageValidationResult = ValidationResult<SurveyImageValidatableKey>;

type SurveyImageStatus = 'idle' | 'loading' | 'saving' | 'failed';

interface ISurveyImageState {
	status: SurveyImageStatus;
	dirty: boolean;
	file?: File;
	surveyImage: SurveyImage;
	originalSurveyImage: SurveyImage;
	validationResult: SurveyImageValidationResult;
}

const defaultSurveyImage: SurveyImage = {
	surveyId: 0,
	image: '',
	displayName: '',
	placement: SurveyLogoPlacement.Left,
	editState: IAction.None,
};

const initialState: ISurveyImageState = {
	status: 'idle',
	dirty: false,
	surveyImage: { ...defaultSurveyImage },
	originalSurveyImage: { ...defaultSurveyImage },
	validationResult: { isValid: true, failures: {} },
};

const loadSurveyImage = createAsyncThunk('surveyTheme/loadSurveyImage', SurveyImageAPI.getSurveyImage);

const saveSurveyImage = createAsyncThunk('surveyImage/saveSurveyImage', async (surveyId: number, { getState }) => {
	const {
		surveyImage: { dirty, surveyImage, file },
	} = getState() as IApplicationState;

	if (!dirty) {
		return surveyImage;
	}

	const data = new FormData();
	if (file) {
		data.append('file', file);
	}
	data.append('surveyImage', JSON.stringify(surveyImage));
	const response = await axios.post<SurveyImage>(Constants.API_SURVEY_IMAGE_URL.replace('{surveyId}', surveyId.toString()), data);
	return response.data;
});

const surveyImageSlice = createSlice({
	name: 'surveyImage',
	initialState: initialState,
	reducers: {
		updateFile: (state, action: PayloadAction<File>) => {
			const { payload: file } = action;

			const surveyImage = {
				...state.surveyImage,
				image: undefined,
				editState: state.surveyImage.surveyImageId ? IAction.Update : IAction.Insert,
			};
			const dirty = true;
			const validationResult = validateSurveyImage(surveyImage, file);

			return {
				...state,
				dirty,
				file,
				surveyImage,
				validationResult,
			};
		},
		updateDisplayName: (state, action: PayloadAction<string>) => {
			const { payload: displayName } = action;

			const surveyImage = {
				...state.surveyImage,
				displayName,
				editState: state.surveyImage.surveyImageId ? IAction.Update : IAction.Insert,
			};
			const dirty = !_.isEqual(state.originalSurveyImage, surveyImage);
			const validationResult = validateSurveyImage(surveyImage, state.file);

			return {
				...state,
				dirty,
				surveyImage,
				validationResult,
			};
		},
		updatePlacement: (state, action: PayloadAction<SurveyLogoPlacement>) => {
			const { payload: placement } = action;

			const surveyImage = {
				...state.surveyImage,
				placement,
				editState: state.surveyImage.surveyImageId ? IAction.Update : IAction.Insert,
			};
			const dirty = !_.isEqual(state.originalSurveyImage, surveyImage);
			const validationResult = validateSurveyImage(surveyImage, state.file);

			return {
				...state,
				dirty,
				surveyImage,
				validationResult,
			};
		},
		removeSurveyImage: (state) => {
			const surveyImage = {
				...state.surveyImage,
				image: undefined,
				displayName: '',
				placement: SurveyLogoPlacement.Left,
				editState: state.surveyImage.surveyImageId ? IAction.Delete : IAction.None,
			};
			const dirty = !!state.surveyImage.surveyImageId;
			const validationResult = validateSurveyImage(surveyImage);

			return { ...state, dirty, file: undefined, surveyImage, validationResult };
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(loadSurveyImage.pending, (state) => {
				state.status = 'loading';
			})
			.addCase(saveSurveyImage.pending, (state) => {
				state.status = 'saving';
			})
			.addMatcher(isAnyOf(loadSurveyImage.fulfilled, saveSurveyImage.fulfilled), (state, action) => {
				state.status = 'idle';
				state.dirty = false;
				state.surveyImage = action.payload !== undefined ? { ...action.payload } : { ...defaultSurveyImage };
				state.originalSurveyImage = action.payload !== undefined ? { ...action.payload } : { ...defaultSurveyImage };
				state.file = undefined;
				state.validationResult = { isValid: true, failures: {} };
			})
			.addMatcher(isAnyOf(loadSurveyImage.rejected, saveSurveyImage.rejected), (state) => {
				state.status = 'failed';
			});
	},
});

const validateSurveyImage = ({ displayName, image }: SurveyImage, file?: File): ValidationResult<SurveyImageValidatableKey> => {
	const validationFailures: ValidationFailures<SurveyImageValidatableKey> = {};

	if ((image || file) && !displayName) {
		validationFailures['displayName'] = ['Client logo name is required.'];
	}

	if (file) {
		const fileValidationErrors = [];

		if (file.size / 1_024 > 200) {
			fileValidationErrors.push('Client logo size must not exceed 200KB.');
		}

		if (file.type !== 'image/bmp' && file.type !== 'image/gif' && file.type !== 'image/jpeg' && file.type !== 'image/png') {
			fileValidationErrors.push('Client logo must be in the format bmp, jpg, png or gif.');
		}

		if (fileValidationErrors.length > 0) {
			validationFailures['file'] = fileValidationErrors;
		}
	}

	return {
		isValid: Object.keys(validationFailures).length === 0,
		failures: validationFailures,
	};
};

export { type ISurveyImageState, initialState, loadSurveyImage, saveSurveyImage, type SurveyImageValidationResult };
export const { updateFile, updateDisplayName, updatePlacement, removeSurveyImage } = surveyImageSlice.actions;
export default surveyImageSlice.reducer;
